import { useCallback, useEffect, useState } from "react";
import type { Persona, QueryKind, QueryRow, Plan } from "./database.types";
import { apiFetch, ApiError } from "./api";

// usePersonas loads the personas (+ their queries) for one site and exposes the
// M3 mutations. Quota (caps/usage) comes straight from the API so the UI can
// show "x / N" and block at-cap actions; the server enforces the caps too.

export interface PersonaWithQueries extends Persona {
  queries: QueryRow[];
}

export interface Caps {
  personas: number;
  queries: number;
}
export interface Usage {
  personas: number;
  queries: number;
}

export interface SuggestedPersona {
  name: string;
  description: string;
  queries: { text: string; kind: QueryKind }[];
}

export interface CreatePersonaInput {
  name: string;
  description?: string | null;
  avatar?: string | null;
  queries?: { text: string; kind: QueryKind }[];
}

interface PersonasState {
  personas: PersonaWithQueries[];
  caps: Caps;
  usage: Usage;
  plan: Plan | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createPersona: (input: CreatePersonaInput) => Promise<void>;
  updatePersona: (
    id: string,
    patch: { name?: string; description?: string | null },
  ) => Promise<void>;
  deletePersona: (id: string) => Promise<void>;
  addQuery: (personaId: string, q: { text: string; kind: QueryKind }) => Promise<void>;
  deleteQuery: (id: string) => Promise<void>;
  suggest: () => Promise<{ provider: string; personas: SuggestedPersona[] }>;
}

const EMPTY_CAPS: Caps = { personas: 0, queries: 0 };

export function usePersonas(token: string, siteId: string | null): PersonasState {
  const [personas, setPersonas] = useState<PersonaWithQueries[]>([]);
  const [caps, setCaps] = useState<Caps>(EMPTY_CAPS);
  const [usage, setUsage] = useState<Usage>({ personas: 0, queries: 0 });
  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!siteId) {
      setPersonas([]);
      setLoading(false);
      return;
    }
    try {
      const res = await apiFetch<{
        personas: PersonaWithQueries[];
        caps: Caps;
        usage: Usage;
        plan: Plan;
      }>(`/api/sites/${siteId}/personas`, token);
      setPersonas(res.personas);
      setCaps(res.caps);
      setUsage(res.usage);
      setPlan(res.plan);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : "load_failed");
    } finally {
      setLoading(false);
    }
  }, [token, siteId]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  const createPersona = useCallback(
    async (input: CreatePersonaInput) => {
      if (!siteId) return;
      await apiFetch(`/api/sites/${siteId}/personas`, token, { method: "POST", body: input });
      await refresh();
    },
    [token, siteId, refresh],
  );

  const updatePersona = useCallback(
    async (id: string, patch: { name?: string; description?: string | null }) => {
      await apiFetch(`/api/personas/${id}`, token, { method: "PATCH", body: patch });
      await refresh();
    },
    [token, refresh],
  );

  const deletePersona = useCallback(
    async (id: string) => {
      await apiFetch(`/api/personas/${id}`, token, { method: "DELETE" });
      await refresh();
    },
    [token, refresh],
  );

  const addQuery = useCallback(
    async (personaId: string, q: { text: string; kind: QueryKind }) => {
      await apiFetch(`/api/personas/${personaId}/queries`, token, { method: "POST", body: q });
      await refresh();
    },
    [token, refresh],
  );

  const deleteQuery = useCallback(
    async (id: string) => {
      await apiFetch(`/api/queries/${id}`, token, { method: "DELETE" });
      await refresh();
    },
    [token, refresh],
  );

  const suggest = useCallback(async () => {
    if (!siteId) return { provider: "", personas: [] };
    const res = await apiFetch<{ provider: string; personas: SuggestedPersona[] }>(
      `/api/sites/${siteId}/suggest`,
      token,
      { method: "POST", body: {} },
    );
    return { provider: res.provider, personas: res.personas };
  }, [token, siteId]);

  return {
    personas,
    caps,
    usage,
    plan,
    loading,
    error,
    refresh,
    createPersona,
    updatePersona,
    deletePersona,
    addQuery,
    deleteQuery,
    suggest,
  };
}
